<template>
  <div class="market-rent-summary">
    <div class="grid-controls">
      <div class="historicals-controls">
        <res-radio-group label="Historicals"
                         :value="historicalDataYearsDisplayed"
                         :options="historicalRadioOptions"
                         @input="historicalDataYearsDisplayedChanged">
        </res-radio-group>
      </div>
      <div class="add-columns-controls">
        <span class="add-columns-label">Add Columns</span>
        <v-checkbox class="add-columns-option"
                    color="var(--v-resBlue-darken1)"
                    label="YoY Variance $"
                    v-model="showVarianceAmountColumn"
                    @change="toggleVarianceAmountColumnVisibility">
        </v-checkbox>
        <v-checkbox class="add-columns-option"
                    color="var(--v-resBlue-darken1)"
                    label="YoY Variance %"
                    v-model="showVariancePercentageColumn"
                    @change="toggleVariancePercentageColumnVisibility">
        </v-checkbox>
      </div>
      <div class="help-controls" @click="openHelp(null)">
        <span class="help-label">Help</span>
        <v-icon color="var(--v-resBlue-darken1)" class="pb-1" small>mdi-help-circle</v-icon>
      </div>
      <res-dialog :showDialog="showHelp"
                  titleClass="resBlue darken-1 white--text"
                  iconColor="white"
                  cancelButtonText="Close"
                  :cancelButtonOnly="true"
                  cancelButtonColor="resBlue darken-1"
                  :maxWidth="800"
                  :setWidth="800"
                  :setHeight="600"
                  attach=".market-rent-summary"
                  @cancel="closeHelp">
        <market-rent-summary-help :openSection="openHelpSection"></market-rent-summary-help>
      </res-dialog>
    </div>
    <ag-grid-vue 
      style="width: 100%;"
      class="ag-theme-material"
      :gridOptions="gridOptions"
      :columnDefs="currentHeaders"
      :rowData="currentData"
      :isExternalFilterPresent="shouldRowsBeFiltered"
      :doesExternalFilterPass="shouldShowRow">
    </ag-grid-vue>
  </div>
</template>

<script>
  import DataFormatter from '@/mixins/DataFormatter';
  import YearGrid from '@/mixins/YearGrid';
  import ResDialog from '@/components/base/ResDialog.vue';

  import ResRadioGroup from "@/components/base/ResRadioGroup";
  import { AgGridVue } from "ag-grid-vue";
  import GroupedRowButton from '@/components/base/ag-grid/GroupedRowButton';

  import MarketRentSummaryHelp from '@/components/help/MarketRentSummaryHelp.vue';
  import HelpableMixin from '@/mixins/Helpable.js';

  import budgetService from '@/services/budget';
  import marketRentService from '@/services/market-rent';
  import { mapGetters, mapActions } from 'vuex';

  export default {
    name: 'MarketRentSummary',
    mixins: [
      DataFormatter, 
      YearGrid,
      HelpableMixin
    ],
    components: {
      ResRadioGroup,
      AgGridVue,
      ResDialog,
      MarketRentSummaryHelp,
      /* eslint-disable vue/no-unused-components */
      GroupedRowButton // used by AG Grid
      /* eslint-enable vue/no-unused-components */
    },
    props: {
      budgetIterationId: { 
        required: true
      }
    },
    data: function () {
      return {
        historicalRadioOptions: [
          { label: 'None', value: 0 },
          { label: '1 Year', value: 1 },
          { label: '2 Years', value: 2 },
          { label: '3 Years', value: 3 }
        ],
        historicalDataYearsDisplayed: 0, // default to hiding history
        fiscalYear: null,
        fiscalYearStartMonth: null,
        availableColumns: [],
        gridOptions: {
          headerHeight: 36,
          rowHeight: 36,
          domLayout: 'autoHeight',
          columnDefs: this.availableColumns,
          alignedGrids: [],
          getRowClass: this.getRowClass
        },
        marketRentData: [],
        showVarianceAmountColumn: false, // default to hiding variance amount column
        showVariancePercentageColumn: false, // default to hiding variance percentage column
      };
    },
    methods: {
      ...mapActions(['changeLoading']),
      async load() {
        await this.loadGridConfiguration();
        await this.loadMarketRentSummary();
        this.sizeGridColumns(this.gridOptions);
      },
      async loadGridConfiguration() {
        let t = this;
        t.changeLoading(true);

        let settings = await budgetService.getBudgetSettings(t.budgetIterationId);
        t.fiscalYear = settings.year;
        t.fiscalYearStartMonth = settings.startMonth;
        
        // setup column headers
        let months = t.getFiscalYearMonths(t.fiscalYearStartMonth);
        t.availableColumns = [
          { headerName: "", field: "showHistoricalRecords", width: 16, pinned: 'left', suppressSizeToFit: true, cellStyle: { margin: 0, padding: 0 },
            cellRendererFramework: 'GroupedRowButton',
            cellRendererParams: {
              show: function(params) { 
                return params.data.hasHistoricalRecords && t.historicalDataYearsDisplayed > 0;
              },
              allCollapsed: function(params) {
                return !params.data.showHistoricalRecords || t.historicalDataYearsDisplayed <= 0;
              },
              onClick: function(params) {
                if (t.historicalDataYearsDisplayed <= 0) {
                  return; // nothing to really do when historicals are not being shown
                }
                if (params.data.hasHistoricalRecords) {
                  let show = !params.data.showHistoricalRecords;
                  params.api.forEachNode(node => {
                    if (node.data.tag == params.data.tag) {
                      node.setDataValue('showHistoricalRecords', show);
                    }
                  });
                  t.refreshDataFilter();
                }
              }
            }
          },
          { headerName: "", field: "label", width: 160, pinned: 'left', suppressSizeToFit: true, cellStyle: { 'margin-left': 0, 'padding-left': 0, 'font-weight': 'bold', 'text-align': 'right' } },
          { headerName: "", field: "year", width: 60, pinned: 'left', suppressSizeToFit: true, cellStyle: { 'margin-right': 0, 'margin-left': 0, 'padding-right': 0, 'padding-left': 0, 'font-weight': 'bold' } },
          { headerName: months[0], field: "periodIndex00Amount", width: 96 },
          { headerName: months[1], field: "periodIndex01Amount", width: 96 },
          { headerName: months[2], field: "periodIndex02Amount", width: 96 },
          { headerName: months[3], field: "periodIndex03Amount", width: 96 },
          { headerName: months[4], field: "periodIndex04Amount", width: 96 },
          { headerName: months[5], field: "periodIndex05Amount", width: 96 },
          { headerName: months[6], field: "periodIndex06Amount", width: 96 },
          { headerName: months[7], field: "periodIndex07Amount", width: 96 },
          { headerName: months[8], field: "periodIndex08Amount", width: 96 },
          { headerName: months[9], field: "periodIndex09Amount", width: 96 },
          { headerName: months[10], field: "periodIndex10Amount", width: 96 },
          { headerName: months[11], field: "periodIndex11Amount", width: 96 },
          { headerName: "TOTAL/AVG", field: "total", width: 96, pinned: 'right' },
          { headerName: "Var. $", field: "varAmount", width: 96, pinned: 'right', hide: !this.showVarianceAmountColumn },
          { headerName: "Var. %", field: "varPercent", width: 96, pinned: 'right', hide: !this.showVariancePercentageColumn }
        ];

        t.changeLoading(false);
      },
      async loadMarketRentSummary() {
        this.changeLoading(true);

        let data = await marketRentService.getMarketRentSummary(this.budgetIterationId, true);

        this.marketRentData = [];
        this.marketRentData.push(this.initRow(data.baseRent, 'base-rent'));
        this.marketRentData.push(this.initRow(data.renovationPremiums, 'renovation-premiums'));
        this.marketRentData.push(this.initRow(data.amenityPremiums, 'amenity-premiums'));
        this.marketRentData.push(this.initRow(data.grossPotentialRent, 'total-gpr'));
        if (data.grossPotentialRent.hasHistoricals) {
          (data.grossPotentialRent.historicals).forEach((item) => {
            this.marketRentData.push(this.initRow(item, 'total-gpr'));
          });
        }
        this.marketRentData.push(this.initAveragesRow(data.averageGrossPotentialRentPerUnit, 'ave-gpr-unit'));
        if (data.averageGrossPotentialRentPerUnit.hasHistoricals) {
          (data.averageGrossPotentialRentPerUnit.historicals).forEach((item) => {
            this.marketRentData.push(this.initAveragesRow(item, 'ave-gpr-unit'));
          });
        }
        this.marketRentData.push(this.initAveragesRow(data.averageGrossPotentialRentPerSquareFoot, 'ave-gpr-sq-ft', true));
        if (data.averageGrossPotentialRentPerSquareFoot.hasHistoricals) {
          (data.averageGrossPotentialRentPerSquareFoot.historicals).forEach((item) => {
            this.marketRentData.push(this.initAveragesRow(item, 'ave-gpr-sq-ft', true));
          });
        }
        
        this.changeLoading(false);
      },
      initRow(data, tag, isPerSquareFoot) {
        return {
          tag: tag,
          year: data.year, 
          label: data.label,
          periodIndex00Amount: this.formatCurrency(data.periodIndex00.amount, !isPerSquareFoot, true),
          periodIndex01Amount: this.formatCurrency(data.periodIndex01.amount, !isPerSquareFoot, true),
          periodIndex02Amount: this.formatCurrency(data.periodIndex02.amount, !isPerSquareFoot, true),
          periodIndex03Amount: this.formatCurrency(data.periodIndex03.amount, !isPerSquareFoot, true),
          periodIndex04Amount: this.formatCurrency(data.periodIndex04.amount, !isPerSquareFoot, true),
          periodIndex05Amount: this.formatCurrency(data.periodIndex05.amount, !isPerSquareFoot, true),
          periodIndex06Amount: this.formatCurrency(data.periodIndex06.amount, !isPerSquareFoot, true),
          periodIndex07Amount: this.formatCurrency(data.periodIndex07.amount, !isPerSquareFoot, true),
          periodIndex08Amount: this.formatCurrency(data.periodIndex08.amount, !isPerSquareFoot, true),
          periodIndex09Amount: this.formatCurrency(data.periodIndex09.amount, !isPerSquareFoot, true),
          periodIndex10Amount: this.formatCurrency(data.periodIndex10.amount, !isPerSquareFoot, true),
          periodIndex11Amount: this.formatCurrency(data.periodIndex11.amount, !isPerSquareFoot, true),
          priorYearTotal: this.formatCurrency(data.priorYearTotal, !isPerSquareFoot, true),
          total: this.formatCurrency(data.total, !isPerSquareFoot, true),
          varAmount: this.formatCurrency(data.varianceAmount, !isPerSquareFoot, true),
          varPercent: this.formatPercentage(data.variancePercentage, true, true),

          // historical groupings
          hasHistoricalRecords: data.hasHistoricals,
          isHistoricalRecord: !data.hasHistoricals && data.year < this.fiscalYear,
          showHistoricalRecords: this.historicalDataYearsDisplayed > 0,
          historicalRecordAge: this.fiscalYear - data.year
        }
      },
      initAveragesRow(data, tag, isPerSquareFoot) {
        return {
          tag: tag,
          year: data.year,
          label: data.label,
          periodIndex00Amount: this.formatCurrency(data.periodIndex00.amount, !isPerSquareFoot, true),
          periodIndex01Amount: this.formatCurrency(data.periodIndex01.amount, !isPerSquareFoot, true),
          periodIndex02Amount: this.formatCurrency(data.periodIndex02.amount, !isPerSquareFoot, true),
          periodIndex03Amount: this.formatCurrency(data.periodIndex03.amount, !isPerSquareFoot, true),
          periodIndex04Amount: this.formatCurrency(data.periodIndex04.amount, !isPerSquareFoot, true),
          periodIndex05Amount: this.formatCurrency(data.periodIndex05.amount, !isPerSquareFoot, true),
          periodIndex06Amount: this.formatCurrency(data.periodIndex06.amount, !isPerSquareFoot, true),
          periodIndex07Amount: this.formatCurrency(data.periodIndex07.amount, !isPerSquareFoot, true),
          periodIndex08Amount: this.formatCurrency(data.periodIndex08.amount, !isPerSquareFoot, true),
          periodIndex09Amount: this.formatCurrency(data.periodIndex09.amount, !isPerSquareFoot, true),
          periodIndex10Amount: this.formatCurrency(data.periodIndex10.amount, !isPerSquareFoot, true),
          periodIndex11Amount: this.formatCurrency(data.periodIndex11.amount, !isPerSquareFoot, true),
          priorYearTotal: this.formatCurrency(data.priorYearAverage, !isPerSquareFoot, true),
          total: this.formatCurrency(data.average, !isPerSquareFoot, true),
          varAmount: this.formatCurrency(data.varianceAmount, !isPerSquareFoot, true),
          varPercent: this.formatPercentage(data.variancePercentage, true, true),

          // historical groupings
          hasHistoricalRecords: data.hasHistoricals,
          isHistoricalRecord: !data.hasHistoricals && data.year < this.fiscalYear,
          showHistoricalRecords: this.historicalDataYearsDisplayed > 0,
          historicalRecordAge: this.fiscalYear - data.year
        }
      },
      getRowClass(params) {
        return params.data.isHistoricalRecord ? 'readonly-row historical' : 'readonly-row';
      },
      shouldRowsBeFiltered() {
        return true;
      },
      shouldShowRow(node) {
        return node.data.isHistoricalRecord == false || 
          (node.data.showHistoricalRecords && node.data.historicalRecordAge <= this.historicalDataYearsDisplayed);
      },
      historicalDataYearsDisplayedChanged(yearsDisplayed) {
        this.historicalDataYearsDisplayed = yearsDisplayed;
        this.gridOptions.api.forEachNode(node => {
            node.setDataValue('showHistoricalRecords', true);          
        });
        this.refreshDataFilter();
      },
      refreshDataFilter() {
        this.gridOptions.api.onFilterChanged();
      },
      toggleVarianceAmountColumnVisibility() {
        this.gridOptions.columnApi.setColumnVisible('varAmount', this.showVarianceAmountColumn);
        this.sizeGridColumns(this.gridOptions);
      },
      toggleVariancePercentageColumnVisibility() {
        this.gridOptions.columnApi.setColumnVisible('varPercent', this.showVariancePercentageColumn);
        this.sizeGridColumns(this.gridOptions);
      }
    },
    computed: {
      ...mapGetters(['currentLoading']),
      currentData() {
        return this.marketRentData;
      },
      currentHeaders() { 
        return this.availableColumns;
      }
    },
    watch: {
      currentLoading: function () {
        if (this.currentLoading == true) {
          this.showGridLoadingOverlay(this.gridOptions);
        } else {
          this.hideGridLoadingOverlay(this.gridOptions);
        }
      }
    },
    async mounted() {
      await this.load(true);
    },
    async created() {
      this.$eventHub.$on('budgetSettingsUpdated', this.load);
      this.$eventHub.$on('baseRentEntriesUpdated', this.loadMarketRentSummary);
      this.$eventHub.$on('renovationPremiumEntriesUpdated', this.loadMarketRentSummary);
      this.$eventHub.$on('amenityPremiumEntriesUpdated', this.loadMarketRentSummary);
    }
  };
</script>

<style lang="scss" scoped>
.v-input--selection-controls::v-deep {
  margin: 0 !important;
  padding: 0 !important;
}
.grid-controls {
  height: 40px;
  margin-bottom: 14px;
}
.historicals-controls {
  display: inline-block; 
  border: solid 1px var(--v-resGrey-lighten3); 
  padding: 6px 0 0 10px; 
  height: 100%;
}
.add-columns-controls {
  display: inline-block; 
  border: solid 1px var(--v-resGrey-lighten3); 
  vertical-align: top; 
  height: 100%;
}
.add-columns-label {
  vertical-align: top;
  display: inline-block;
  padding: 7px 8px 0 10px; 
  height: 100%;
}
.add-columns-option::v-deep {
  display: inline-block;
  .v-label {
    font-size: 13px !important;
    color: var(--v-resGrey-darken2);
    font-weight: bold;
    padding-right: 14px;
    padding-top: 8px;
  }
  .v-input--selection-controls__input {
    margin-top: 0;
    padding-top: 8px;
  }
  .v-messages {
    display: none;
  }
}
  .help-controls {
    height: 100%;
    float: right;
    border: solid 1px var(--v-resGrey-lighten3);
    padding: 7px 6px 6px 6px;
    cursor: pointer;
  }
.help-label {
  padding-right: 6px;
}
</style>
